import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { cashupService } from "../../_services";
import moment from 'moment';

// Import components
import TopHeader from "../template/TopHeader";
import DateRangePicker from "./DateRangePicker";
import CashupTable from "./CashupTable";
import Pagination from "react-js-pagination";

// Import utilities
import { 
  getDefaultDateRange, 
  createCashupParams, 
  createNewCashupObject 
} from "./CashupUtils";

// Import styles
import "./Cashup.css";

/**
 * Cashup component for managing cash up records
 */
const Cashup = (props) => {
  // State
  const [cashups, setCashups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Navigation
  const goToPrevPage = () => {
    props.history.push('/transactions');
  };

  const navigateToCashupDetail = (cashupId) => {
    props.history.push(`/cashup/detail/${cashupId}`);
  };

  // Fetch data when page or date range changes
  useEffect(() => {
    fetchCashupHistory();
  }, [pagination.page, dateRange]);

  /**
   * Create a new cashup for today
   */
  const createTodayCashup = () => {
    setLoading(true);
    
    const newCashup = createNewCashupObject();
    
    cashupService.createCashup(newCashup)
      .then(response => {
        if (response.data && response.data.status === 1) {
          // Navigate to the detail page with the new cashup ID
          const cashupId = response.data.data._id;
          props.history.push(`/cashup/detail/${cashupId}`);
        } else {
          console.error("Error creating cashup:", response.data);
          alert("Failed to create today's cash up: " + (response.data?.message || "Unknown error"));
          setLoading(false);
        }
      })
      .catch(error => {
        console.error("Error creating cashup:", error);
        const errorMessage = error.response?.data?.message || error.message || "Unknown error";
        alert("Failed to create today's cash up: " + errorMessage);
        setLoading(false);
      });
  };

  /**
   * Fetch cashup history with filters
   */
  const fetchCashupHistory = () => {
    setLoading(true);
    
    const params = createCashupParams(pagination, dateRange);
    
    cashupService.getCashupHistory(params)
      .then(response => {
        if (response.data && response.data.status === 1) {
          const cashupData = response.data.data.cashups || [];
          setCashups(cashupData);
          setPagination({
            ...pagination,
            total: response.data.data.total || 0,
            totalPages: Math.ceil((response.data.data.total || 0) / pagination.limit)
          });
        } else {
          console.error("Error fetching cashup history:", response.data);
          setCashups([]);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching cashup history:", error);
        setCashups([]);
        setLoading(false);
      });
  };

  /**
   * Handle page change in pagination
   */
  const handlePageChange = (page) => {
    setPagination({
      ...pagination,
      page
    });
  };

  return (
    <div className="h_n">
      <div className="body_container cm_bg_white _nowhite">
        <TopHeader
          {...props}
          title="Cash Up"
          style={{ position: 'sticky', top: 0, zIndex: 1000 }}
          headerButtonStatus={true}
          cancelRefreshPage={true}
          resetAllSetting={goToPrevPage}
        />
        <div className="new-setting-wrapper">
          <div className="container">
            <div className="row">
              <div className="col-md-12">
                <div className="cashup-table-container">
                  {/* Date Range Selector */}
                  <DateRangePicker 
                    dateRange={dateRange} 
                    setDateRange={setDateRange} 
                  />
                  
                  {/* Cashup Table */}
                  <CashupTable 
                    cashups={cashups}
                    loading={loading}
                    createTodayCashup={createTodayCashup}
                    navigateToCashupDetail={navigateToCashupDetail}
                  />
                  
                  {/* Pagination */}
                  {!loading && cashups.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '40px' }}>
                      <Pagination
                        activePage={pagination.page}
                        itemsCountPerPage={pagination.limit}
                        totalItemsCount={pagination.total}
                        pageRangeDisplayed={4}
                        onChange={handlePageChange}
                        innerClass="pagination-inner"
                        itemClass="page-number"
                        activeClass="active"
                        itemClassPrev="pagination-prev"
                        itemClassNext="pagination-next"
                        linkClassNext="pagination-next-link"
                        prevPageText="◀"
                        nextPageText="▶"
                        hideDisabled={true}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    ...state
  };
}

export default connect(mapStateToProps)(Cashup);
